// BCM-898 · Goods Receipt Dashboard — the digitalized documents a review shows.
//
// One builder feeds both the document tabs in GRDigitalizedDocumentReview (each
// with its review-count badge) and the "N low confidence fields" label on the
// batch row (BatchRow), so the row total is always the sum of the visible tab
// badges — never a count over documents that have no tab.
import { DOC_CATEGORY } from "./constants";
import { isFlagTrue } from "./formatters";

// The documents PA2 (Review of low confidence fields) shows as tabs: the
// category whose field review is pending, read from the same per-stage flags,
// in the same order, resolvePendingAction routes to PA2 on. A batch on the
// pre-shipment (45) or post-shipment (50) field review shows THOSE documents —
// not the Post-Manufacturing ones. The same category feeds the batch row's label
// (reviewSummary → resolvePendingAction.labelFor).
export const pa2CategoryFor = (row) => {
  const r = row || {};
  if (isFlagTrue(r.reviewPostMFGFieldsReqd)) return DOC_CATEGORY.POST_MANUFACTURING;
  if (isFlagTrue(r.reviewPreShipmentFieldsReqd)) return DOC_CATEGORY.PRE_SHIPMENT;
  if (isFlagTrue(r.reviewPostShipmentFieldsReqd)) return DOC_CATEGORY.POST_SHIPMENT;
  return DOC_CATEGORY.POST_MANUFACTURING;
};

// IDoc-sourced ASN documents (CMO ERP file transfer) — no OCR, so no per-field
// confidence. Anything else is an OCR'd file with low confidence fields.
const isAsnDocType = (docType) =>
  docType === "Production ASN" || docType === "Post Shipment ASN";

export const parseMaybe = (x) => {
  if (!x) return null;
  if (typeof x === "object") return x;
  try { return JSON.parse(x); } catch (e) { return null; }
};

// A document_type is normally a plain string, but an IDoc-sourced ASN can carry it
// wrapped like any other field — { confidence, value, isMandatory } — so unwrap to
// the string. Anything else non-string is dropped so the next source is tried.
const asText = (v) => {
  const t = v && typeof v === "object" && !Array.isArray(v) ? v.value : v;
  return typeof t === "string" && t ? t : null;
};

// kvpJsonEdited wins once populated, else kvpJsonOriginal / kvpJson.
export const getKvpData = (doc) => {
  const edited = parseMaybe(doc.kvpJsonEdited);
  if (edited && Object.keys(edited).length > 0) return edited;
  return parseMaybe(doc.kvpJsonOriginal) || parseMaybe(doc.kvpJson) || {};
};

// One entry per digitized document of the batch (A2 detail), narrowed by the
// optional filters:
//   onlyConsumption — documents carrying consumption bowls only;
//   category        — a DOC_CATEGORY value, matched against the document's file
//                     in customFilesList (a record with no matching file has no
//                     category, so it never passes this filter);
//   onlyDocType     — a single document_type, e.g. "CertificateOfManufacture".
export const buildReviewDocs = (detail, { onlyConsumption = false, category, onlyDocType } = {}) => {
  const all = (detail && detail.digitizedExtractedData) || [];
  const files = (detail && detail.customFilesList) || [];
  const fileOf = (cfid) =>
    files.find((x) => String(x.schrockenCustomFileID) === String(cfid)) || null;
  const filtered = all
    .map((doc) => {
      const kvp = getKvpData(doc);
      const file = fileOf(doc.schrockenCustomFileID);
      return {
        doc,
        cfid: doc.schrockenCustomFileID,
        kvp,
        // The document type string CommonDigitizedDocument needs. It matches its
        // internal lookups against `kvpJsonOriginal.document_type`, and sends this
        // as `documentType` in the mark-review payload — so read it from
        // kvpJsonOriginal first, exactly like ViewStructuredInformation does
        // (where customFilesList.customFileType is the same string).
        docType:
          asText((parseMaybe(doc.kvpJsonOriginal) || {}).document_type) ||
          asText(kvp.document_type) ||
          asText(file && file.customFileType) ||
          asText(doc.documentType) ||
          null,
        count: Number(kvp.noOfFieldsToBeReviewed) || 0,
        // Same rule CommonDigitizedDocument uses to show "Mark Review as Complete":
        // the document has (or had) fields to review and isn't marked reviewed yet.
        needsMarkReview:
          kvp.markAsReviewed !== true &&
          ((Number(kvp.noOfFieldsToBeReviewed) || 0) > 0 || (Number(kvp.noOfFieldsReviewed) || 0) > 0),
        hasBowls: Array.isArray(kvp.bowls) && kvp.bowls.length > 0,
        category: file ? file.documentCategoryType : null,
      };
    })
    .filter((d) => (onlyConsumption ? d.hasBowls : true))
    .filter((d) => (category ? d.category === category : true))
    .filter((d) => (onlyDocType ? d.docType === onlyDocType : true));

  // BCM-898 (Patheon ERP PO / "CMO QC Released" fix): an IDoc-sourced ASN document
  // (Production ASN / Post Shipment ASN) already carries this batch's full
  // consumption data and supersedes every other document in its category — once
  // one is present, hide the individual siblings so only the ASN tab shows.
  if (!filtered.some((d) => isAsnDocType(d.docType))) return filtered;
  const asnOnly = filtered.filter((d) => isAsnDocType(d.docType));

  // Concurrent shipments of the SAME ASN doc type (each tagged with its own
  // deliveryNumber) collapse into ONE outer tab — CommonDigitizedDocument renders
  // its own "Shipment 1 / Shipment 2" tabs internally. A doc type with no
  // deliveryNumber keeps one tab per record.
  const seenShipmentDocTypes = new Set();
  return asnOnly
    .slice()
    .sort((a, b) => (a.cfid || 0) - (b.cfid || 0))
    .filter((d) => {
      if (!d.doc.deliveryNumber) return true;
      if (seenShipmentDocTypes.has(d.docType)) return false;
      seenShipmentDocTypes.add(d.docType);
      return true;
    });
};

// What the review under way (`category`, see pa2CategoryFor) holds, read from the
// same document list the PA2 tabs are built from:
//   isAsn — an IDoc-sourced ASN is among them (the batch row alone doesn't say,
//           so this is only ever true once the A2 `detail` has loaded);
//   count — the sum of the tab badges, i.e. the low confidence fields still to
//           review (documents with no tab are not counted).
export const reviewSummary = (detail, category) => {
  const docs = buildReviewDocs(detail, { category });
  return {
    isAsn: docs.some((d) => isAsnDocType(d.docType)),
    count: docs.reduce((sum, d) => sum + d.count, 0),
  };
};
